// ** Modules
import React from 'react';

// ** Components
import NewsCarousel, { type NewsCarouselItem } from '@/components/strapi/Blocks/ComponentBlocksNewsBlock/NewsCarousel';

// ** Utils
import { useGetNews } from '@/lib/utils/newsDataUtils';
import { normalizeDateLabel } from '@/utils/shared/stringUtils';
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { AppContext } from '@/types/contextTypes';
import type { StrapiComponentElementProps } from '@/types/strapi';
import type { News, ComponentBlocksCarouselBlock } from '@/types/graphql';

type NewsCarouselVariant = StrapiComponentElementProps<Omit<ComponentBlocksCarouselBlock, 'carouselType'>> & {
	carouselSectionTitle?: string;
	extraProps?: {
		newsBasePath?: string;
		eventsBasePath?: string;
	};
};

const NewsCarouselVarian = ({
	host,
	locale,
	extraProps,
	carouselCta,
	carouselShowCta,
	carouselShowTitle,
	carouselSectionTitle,
	carouselShowArrowControls
}: NewsCarouselVariant) => {
	const { allNews_connection, loading } = useGetNews({
		locale: locale ?? 'de',
		pagination: { page: 1, pageSize: 9, limit: null, start: null },
		sort: ['publishedAt:desc'],
		filters: null,
		hasPublishedVersion: true,
		publicationFilter: 'HAS_PUBLISHED_VERSION',
		status: 'PUBLISHED'
	});

	const news: NewsCarouselItem[] = React.useMemo(() => {
		const nodes = allNews_connection?.nodes || [];
		const dynamicPath = extraProps?.newsBasePath ?? '';
		const safeHost: AppContext['host'] = host || (typeof window !== 'undefined' ? window.location.origin : '');

		return nodes.map((item: News) => {
			const cleanDate = normalizeDateLabel(item?.publishedAt ? `${item.publishedAt}` : undefined);

			return {
				title: item.newsTitle || 'Unbekannter Titel',
				description: item.newsDescription || '',
				publishedAt: cleanDate,
				slug: item.slug,
				imageData: item.newsBild,
				host: safeHost,
				ctaLabel: 'Zum Artikel',
				typeLabel: item.newsCategory || 'News',
				basePath: dynamicPath
			};
		});
	}, [allNews_connection, host, extraProps?.newsBasePath]);

	// const carouselHref = carouselCta?.hrefExternal
	// 	? carouselCta.hrefExternal
	// 	: buildPathFromRelationLink(carouselCta?.href);

	const carouselHref = React.useMemo(() => {
		if (carouselCta?.hrefExternal) return carouselCta.hrefExternal;

		const manualLink = buildPathFromRelationLink(carouselCta?.href);
		if (manualLink !== '/') return manualLink;

		// Only inject /news/ if it's a detail page (slug !== base)
		const newsSegment = extraProps?.newsBasePath?.endsWith('/news') ? '' : '/news';
		return `${extraProps?.newsBasePath}${newsSegment}/`;
	}, [carouselCta, extraProps]);

	return (
		<React.Fragment>
			{!loading && ( // TODO: Add skeleton loader for title and CTA
				<NewsCarousel
					items={news}
					cardsPerView={{ xs: 1.2, md: 2.5, lg: 3.5 }}
					title={carouselSectionTitle}
					showTitle={carouselShowTitle}
					ctaLabel={carouselCta?.label || 'Alle ansehen'}
					ctaHref={carouselHref}
					showCta={carouselShowCta}
					showArrowControls={carouselShowArrowControls}
					prevAriaLabel={'Vorherige News'}
					nextAriaLabel={'Naechste News'}
					basePath={carouselHref}
				/>
			)}
		</React.Fragment>
	);
};

export default NewsCarouselVarian;
